import React, { useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion, useMotionValueEvent, useScroll } from "framer-motion";
import { useLang } from "../Utility/Languagecontext";
import { COMPANY, whatsappUrl } from "../Data/Company";
import { scrollToTop } from "../Utility/scroll";

// الأرقام بقت من src/Data/Company.js والنصوص من ملفات اللغات (t.fab)

const EASE = [0.76, 0, 0.24, 1];

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor" aria-hidden="true">
    <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38a9.9 9.9 0 0 0 4.74 1.21h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2Zm0 18.15h-.01a8.2 8.2 0 0 1-4.18-1.14l-.3-.18-3.12.82.83-3.04-.2-.31a8.2 8.2 0 0 1-1.26-4.39c0-4.54 3.7-8.24 8.25-8.24 2.2 0 4.27.86 5.82 2.42a8.18 8.18 0 0 1 2.41 5.83c0 4.54-3.7 8.23-8.24 8.23Zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.12-.16.25-.64.81-.78.97-.14.17-.29.19-.54.06-.25-.12-1.05-.39-1.99-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.02-.38.11-.5.11-.11.25-.29.37-.43.12-.14.16-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.4-.42-.56-.43h-.48c-.17 0-.43.06-.66.31-.22.25-.86.85-.86 2.07 0 1.22.89 2.4 1.01 2.56.12.17 1.75 2.67 4.23 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.67-1.18.21-.58.21-1.07.14-1.18-.06-.1-.22-.16-.47-.28Z" />
  </svg>
);

const PhoneIcon = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2Z" />
  </svg>
);

const ArrowUp = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 19V5M6 11l6-6 6 6" />
  </svg>
);

const ArrowBack = () => (
  <svg className="fab__back-ic" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M19 12H5M11 6l-6 6 6 6" />
  </svg>
);

const pop = {
  initial: { opacity: 0, scale: 0.6, y: 12 },
  animate: { opacity: 1, scale: 1, y: 0 },
  exit: { opacity: 0, scale: 0.6, y: 12 },
  transition: { duration: 0.45, ease: EASE },
};

export const FloatingButtons = () => {
  const { t } = useLang();
  const f = t.fab;
  const { pathname } = useLocation();
  const navigate = useNavigate();

  // زرار "العودة للأعلى" بيظهر بعد ما تنزل شوية
  const { scrollY } = useScroll();
  const [showTop, setShowTop] = useState(false);
  useMotionValueEvent(scrollY, "change", (y) => setShowTop(y > 700));

  // زرار "رجوع" بيظهر في كل الصفحات ما عدا الرئيسية
  const showBack = pathname !== "/";
  const goBack = () => {
    const idx = window.history.state && window.history.state.idx;
    if (typeof idx === "number" && idx > 0) navigate(-1);
    else navigate("/");
  };

  // Portal: بيتعرض مباشرة في body فمفيش أب عليه transform يبوّظ الـ fixed
  return createPortal(
    <>
      <div
        className="fab"
        role="group"
        aria-label={f.group}
        style={{
          position: "fixed",
          insetInlineEnd: "1.5rem",
          bottom: "1.5rem",
          zIndex: 2147483000,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "0.8rem",
        }}
      >
        <AnimatePresence>
          {showTop && (
            <motion.button key="top" type="button" className="fab__btn fab__btn--top" aria-label={f.top} onClick={scrollToTop} {...pop}>
              <ArrowUp />
              <span className="fab__tip">{f.top}</span>
            </motion.button>
          )}
        </AnimatePresence>

        <a href={`tel:${COMPANY.phone}`} className="fab__btn fab__btn--call" aria-label={f.call}>
          <PhoneIcon />
          <span className="fab__tip">{f.call}</span>
        </a>

        <a href={whatsappUrl(f.waMessage)} className="fab__btn fab__btn--wa" target="_blank" rel="noopener noreferrer" aria-label={f.whatsapp}>
          <WhatsAppIcon />
          <span className="fab__tip">{f.whatsapp}</span>
        </a>
      </div>

      <AnimatePresence>
        {showBack && (
          <motion.button
            key="back"
            type="button"
            className="fab-back"
            aria-label={f.back}
            onClick={goBack}
            style={{ position: "fixed", insetInlineStart: "1.5rem", bottom: "1.5rem", zIndex: 2147483000 }}
            {...pop}
          >
            <ArrowBack />
            <span>{f.back}</span>
          </motion.button>
        )}
      </AnimatePresence>
    </>,
    document.body
  );
};
